import React, { useCallback, useEffect, useState } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import BackButton from "../../common/back-button";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_CLIENTS_ENDPOINT } from "../../config/urls";

const Field = ({ label, value }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={600}
      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography variant="body1" sx={{ mt: 0.5 }}>
      {value || "—"}
    </Typography>
  </Box>
);

const ClientDetailView = ({ client: initialClient, onBack }) => {
  const { getValidToken } = useAuth();
  const [client, setClient] = useState(initialClient);

  const getHeaders = useCallback(async () => {
    const token  = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [getValidToken]);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const headers = await getHeaders();
        const res = await fetch(`${MFD_CLIENTS_ENDPOINT}${initialClient.id}/`, { headers });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setClient({
          ...data,
          client_name:  data.name    ?? data.client_name,
          phone_number: data.phone_no ?? data.phone_number,
        });
      } catch {
        // keep initial data on error
      }
    };
    fetchDetail();
  }, [initialClient.id, getHeaders]);

  return (
    <Box sx={{ p: 3 }}>
      <BackButton onClick={onBack} />

      <Box sx={{ mt: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Client Details
        </Typography>
        <Typography sx={{ ...typography.bodyLarge, color: "text.secondary", mt: 0.5 }}>
          {client.client_name} &nbsp;·&nbsp; {client.client_code}
        </Typography>
      </Box>

      {/* Identity & Contact */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        Identity &amp; Contact
      </Typography>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="PAN" value={client.pan} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Client Code" value={client.client_code} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Client Code 2" value={client.client_code_2} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Client Name" value={client.client_name} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Country Code" value={client.country_code} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Phone Number" value={client.phone_number} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Email" value={client.email} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Date of Birth" value={client.dob} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Source" value={client.source} />
          </Grid>
        </Grid>
      </Paper>

      {/* Broker */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        Broker
      </Typography>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Broker Name" value={client.broker_name} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Broker Code" value={client.broker_code} />
          </Grid>
        </Grid>
      </Paper>

      {/* Address */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        Address
      </Typography>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Address Line 1" value={client.address_1} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="Address Line 2" value={client.address_2} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Field label="City / State" value={client.address_3} />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ClientDetailView;
